import { useEffect, useState } from 'react';
import { Layout } from './Layout';
import { ListItem } from './ListItem';
import toast, { Toaster } from 'react-hot-toast';
import { TodoType, MessageNames, crudTodo } from '../api';
import { useParams } from 'react-router';
import { onClipboardClick } from './List.utils';
import { Realtime, RealtimeChannel } from 'ably';
import { ListItemSkeleton } from './ListItem.skeleton';

type Listprops = {
  username?: string;
  channelId?: string;
  isNew?: boolean;
};

export const List = ({ isNew }: Listprops) => {
  const { channelId } = useParams();
  const [todos, setTodos] = useState<TodoType[]>([]);
  const [channel, setChannel] = useState<RealtimeChannel | null>(null);
  const [loaded, setLoaded] = useState(false);

  const onTodoFormSubmitted = (formData: any) => {
    const newTodo: TodoType = {
      name: formData.get('todo'),
      isComplete: false,
      id: crypto.randomUUID(),
      channelId,
    };
    publishMessage(MessageNames.AddTodo, newTodo);
    crudTodo({ channelId, type: 'AddTodo', message: newTodo });
  };

  const onCheckboxClick = (id: string) => {
    const currentTodo = todos.find((todo) => todo.id === id)!;
    const newTodo: TodoType = {
      ...currentTodo,
      isComplete: !currentTodo.isComplete,
      channelId,
    };
    const messageType = newTodo.isComplete
      ? MessageNames.CheckTodo
      : MessageNames.UncheckTodo;

    crudTodo({ channelId, type: messageType, message: newTodo });
    publishMessage(messageType, newTodo);
  };

  const publishMessage = (name: MessageNames, payload: any) =>
    channel?.publish(name, payload);

  const getInitialTodos = async () => {
    const response = await fetch('/.netlify/functions/getTodos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        channelId,
      }),
    });
    const data = await response.json();
    setTodos(data.todos as TodoType[]);
    setLoaded(true);
  };

  useEffect(() => {
    getInitialTodos();

    const ably = new Realtime({
      key: 'An-6oQ.sCXEMQ:R-dVFQxa6Z2MptllmNUk0EVOXYqwVONOCRUpXdo0pZc',
    });
    const newChannel = ably.channels.get(`${channelId}`);
    setChannel(newChannel);
    newChannel.publish(MessageNames.NewConnection, {});
    newChannel.subscribe(MessageNames.AddTodo, (message: any) => {
      console.log({ message });
      setTodos((prev) => [...prev, message.data]);
      toast('Todo Added', { icon: '📝' });
    });

    newChannel.subscribe(MessageNames.CheckTodo, (message: any) => {
      const newTodo = message.data as TodoType;
      setTodos((prev) => [
        ...prev.filter((todo) => todo.id !== newTodo.id),
        newTodo,
      ]);
      toast('Todo checked', { icon: '✅' });
    });

    newChannel.subscribe(MessageNames.UncheckTodo, (message: any) => {
      const newTodo = message.data as TodoType;
      setTodos((prev) => [
        ...prev.filter((todo) => todo.id !== newTodo.id),
        newTodo,
      ]);
      toast('Todo Unchecked', { icon: '❌' });
    });

    newChannel.subscribe(MessageNames.NewConnection, (message: any) => {
      toast('New Connection', { icon: '🌐' });
    });

    newChannel.subscribe(MessageNames.NewDisconnection, () => {
      toast('Disconnected', { icon: '⚡' });
    });

    toast('Welcome!', { icon: '🔥' });

    return () => {
      newChannel.unsubscribe();
      console.log('disconnecting');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  const sortedItems = (items: TodoType[]) =>
    items
      ? [...items].sort((a, b) => Number(a.isComplete) - Number(b.isComplete))
      : [];

  const shouldShowSkeletons = !loaded && !isNew;

  return (
    <Layout>
      <div className="flex flex-row items-center justify-center w-full">
        <div className="flex flex-col items-center justify-center w-full max-w-screen-sm gap-2">
          <div className="flex flex-row w-full justify-between items-center">
            <button
              onClick={onClipboardClick}
              className="border rounded-lg p-2 w-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300"
            >
              Click here to copy this list! 📋
            </button>
          </div>
          <form
            className="w-full gap-2 flex flex-row"
            // @ts-ignore
            action={onTodoFormSubmitted}
          >
            <input
              required
              className="w-3/4 border rounded-lg p-2"
              placeholder="Enter todo"
              name="todo"
            />
            <button className="w-1/4 p-2 border rounded-lg bg-gray-100 hover:bg-gray-200 active:bg-gray-300">
              Create Todo
            </button>
          </form>
          <div className="w-full flex flex-col gap-2">
            {!shouldShowSkeletons
              ? sortedItems(todos)?.map((todo) => (
                  <ListItem
                    {...todo}
                    onClick={() => onCheckboxClick(todo.id)}
                    key={todo.id}
                  />
                ))
              : [1, 2, 3, 4].map((e) => <ListItemSkeleton key={e} />)}
          </div>
        </div>
      </div>
      <Toaster position="bottom-left" reverseOrder={false} />
    </Layout>
  );
};
